import useAppSettingsActions from '@/actions/useAppSettingsActions'
import { useAppContext } from '@/AppContext'
import * as Constants from '@/services/Constants'
import DesktopIcon from '@/CustomIcons/DesktopIcon'
import RightChevronIcon from '@/CustomIcons/RightChervron'
import ElectronIPC from '@/services/ElectronIPC'
import { Button } from '@/UIComponents2/Button'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import { Slider } from '@/UIComponents2/Slider'
import { DesktopCapturerSourceExt, useUserContext } from '@/UserContext'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '../../Card'
import { CardHeader } from '../../CardHeader'
import { cn } from '@/utlis/cn'
import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import Video from '@/pages/SessionPage/internal/shared/Video'
import Tooltip from '@/UIComponents2/Tooltip'

export function ScreenShareSection(props: { inSession: boolean }) {
    const [appState, appDispatch] = useAppContext()
    const [userState] = useUserContext()
    const { updateAppSettingItem } = useAppSettingsActions()
    const { t } = useTranslation()
    const [scrollSensitivity, setScrollSensitivity] = useState(appState.appSettings.screenControlWheelScaleFactor)
    const { openShareScreenDialog } = useMediaSelectionActions()

    const sourceInfo = userState.mediaSelection?.desktopSourceInfo

    return (
        <Card className="hidden md:flex">
            <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-6">
                    <CardHeader icon={DesktopIcon} title="Share Screen" />
                    <Tooltip text={props.inSession ? '' : t('Join the session before sharing screen')}>
                        <Button
                            disabled={!props.inSession}
                            variant="secondary"
                            className={cn(
                                'w-full justify-between relative shadow-glow bg-white/1',
                                sourceInfo ? 'text-muse-yellow' : 'text-muse-fg/70',
                                !props.inSession && 'cursor-not-allowed'
                            )}
                            onClick={() => {
                                openShareScreenDialog()
                            }}
                        >
                            {sourceInfo?.name || t('Select Screen to Share')}
                            <RightChevronIcon className="absolute right-4" style={{ fontSize: '1.3rem' }} />
                        </Button>
                    </Tooltip>
                    <div className="flex flex-col gap-4">
                        <Checkbox
                            label="Pin sidebar to top when entering screen share"
                            checked={appState.appSettings.screenShareSideBarAlwaysOnTop}
                            onChange={(v) => {
                                updateAppSettingItem('screenShareSideBarAlwaysOnTop', v)
                            }}
                        />
                        <Checkbox
                            label="Invert mouse wheel during screen control"
                            checked={appState.appSettings.screenControlWheelDirectionInvert}
                            onChange={(v) => {
                                updateAppSettingItem('screenControlWheelDirectionInvert', v)
                                ElectronIPC.setWindowOptions({ isContentProtectionEnabled: !v })
                            }}
                        />
                    </div>
                    <div className="flex flex-col gap-4">
                        <div>Mouse Wheel / Trackpad Sensitivity</div>
                        <div className="grid grid-cols-[1fr_50px] gap-6 items-center">
                            <Slider
                                min={0.1}
                                max={2.0}
                                step={0.1}
                                markers={false}
                                initialValue={appState.appSettings.screenControlWheelScaleFactor ?? 1.0}
                                value={scrollSensitivity}
                                onChange={(v) => setScrollSensitivity(v)}
                                onChangeEnd={(v) => {
                                    appDispatch({
                                        type: 'update_app_settings',
                                        value: {
                                            screenControlWheelScaleFactor: v,
                                        },
                                    })
                                }}
                            />
                            <div className="opacity-75 font-light pr-4">{scrollSensitivity.toFixed(1)}</div>
                        </div>
                    </div>
                    <div className="flex">
                        <ScreenQualitySelect />
                        <ScreenFrameRateSelect />
                    </div>
                </div>
                <div>
                    <div className="w-full aspect-[16/9] bg-neutral-700 rounded-t-[10px]">
                        {userState.mediaStreams.desktop instanceof MediaStream && (
                            <Video
                                className="rounded-t-[10px] rounded-b-none"
                                style={{ width: '100%' }}
                                srcObject={userState.mediaStreams.desktop}
                                isMirrored={false}
                                autoPlay
                                autoPause={3000}
                            />
                        )}
                    </div>
                    <div className="flex justify-center w-full rounded-b-[10px] bg-neutral-800 p-4 t-lg-medium">
                        {sourceInfo ? `${sourceInfo?.name}` : t('Not sharing screen')}
                    </div>
                </div>
            </div>
        </Card>
    )
}

export function ScreenQualitySelect() {
    const [userState] = useUserContext()
    const [appState, appDispatch] = useAppContext()
    const { t } = useTranslation()

    const desktopSources = userState.mediaSources.desktop
    const selectedDesktopSource = userState.mediaSources.desktop.find((a) => a.id === userState.mediaSelection.desktop)

    // the dropdown list (360p, 720p, etc) that gets shown.
    const visibleDesktopVideoHeightOptions: number[] = useMemo(() => {
        return Constants.desktopVideoHeightOptions.filter((h) => {
            let maxScreenHeight: number | null = 0
            if (selectedDesktopSource?.screenHeight) {
                // if `selectedDesktopSource.screenHeight` exists, use that.
                maxScreenHeight = selectedDesktopSource.screenHeight
            } else {
                // otherwise, use the max screen height in all of desktopSources.
                desktopSources.forEach((a) => {
                    if (a.screenHeight && a.screenHeight > (maxScreenHeight || 0)) {
                        maxScreenHeight = a.screenHeight
                    }
                })
                if (maxScreenHeight === 0) {
                    maxScreenHeight = null
                }
            }

            return h <= (maxScreenHeight || Number.MAX_SAFE_INTEGER)
        })
    }, [desktopSources, selectedDesktopSource])

    const options = useMemo(() => {
        let items = [{ value: '_best', label: t('Native') }]
        for (const height of visibleDesktopVideoHeightOptions) {
            items.push({
                value: height.toFixed(0),
                label: renderScreenDimensionWithPercentages(selectedDesktopSource, height),
            })
        }
        return items
    }, [selectedDesktopSource, visibleDesktopVideoHeightOptions])

    const currentSelection = (): string => {
        const optionHeight = appState.appSettings.screenShareDesktopVideoOptions?.optionHeight
        if (optionHeight) {
            if (visibleDesktopVideoHeightOptions.includes(optionHeight)) {
                return optionHeight.toFixed(0)
            } else {
                return '_best'
            }
        }
        return '_best'
    }

    return (
        <SimpleSelect
            className="basis-full md:basis-6/10 p-[2px]"
            label="Quality"
            value={currentSelection()}
            items={options}
            widthMatching
            onChange={(value) => {
                const newQuality = value === '_best' ? null : parseInt(value)
                appDispatch({
                    type: 'update_app_settings',
                    value: {
                        screenShareDesktopVideoOptions: {
                            ...(appState.appSettings.screenShareDesktopVideoOptions || {}),
                            optionHeight: newQuality,
                        },
                    },
                })
            }}
        />
    )
}

export function ScreenFrameRateSelect() {
    const [appState, appDispatch] = useAppContext()
    const { t } = useTranslation()

    const options = [
        { value: '_best', label: t('Default') },
        { value: '60', label: '60 fps' },
        { value: '30', label: '30 fps' },
        { value: '24', label: '24 fps' },
        { value: '20', label: '20 fps' },
        { value: '16', label: '16 fps' },
        { value: '10', label: '10 fps' },
        { value: '8', label: '8 fps' },
        { value: '5', label: '5 fps' },
    ]
    const currentValue = String(appState.appSettings.screenShareDesktopVideoOptions?.optionFramerate ?? '_best')

    return (
        <SimpleSelect
            className="basis-full md:basis-4/10 p-[2px]"
            value={currentValue}
            label="Frame Rate"
            items={options}
            onChange={(value) => {
                const newFrameRate = value === '_best' ? null : parseInt(value)
                appDispatch({
                    type: 'update_app_settings',
                    value: {
                        screenShareDesktopVideoOptions: {
                            ...(appState.appSettings.screenShareDesktopVideoOptions || {}),
                            optionFramerate: newFrameRate,
                        },
                    },
                })
            }}
            widthMatching
        />
    )
}

const renderScreenDimensionWithPercentages = (
    selectedDesktopSource: DesktopCapturerSourceExt | null | undefined,
    height: number,
    options?: { onlyResolution?: boolean }
) => {
    if (height == null || isNaN(height)) {
        return '-'
    }

    const percentageText = height + 'p'

    const info = selectedDesktopSource
    if (info == null || info.screenWidth == null || info.screenHeight == null) {
        if (options?.onlyResolution) {
            return ''
        } else {
            return percentageText
        }
    }

    const resizedWidth = Math.floor((info.screenWidth / info.screenHeight) * height)
    const resizedHeight = height

    if (options?.onlyResolution) {
        return `(${resizedWidth} X ${resizedHeight})`
    } else {
        return `${percentageText}\u3000\u3000(${resizedWidth} X ${resizedHeight})`
    }
}
